import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

const EVENT_OPTIONS = [
    "",
    "view_content",
    "lead",
    "qualified_lead",
    "complete_registration",
    "begin_checkout",
    "purchase",
    "application_approved",
    "contact_whatsapp",
    "contact_telegram",
];

export default function AdminEventLogs() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        event_name: "",
        country_code: "",
        user_email: "",
        start_at: "",
        end_at: "",
        limit: 200,
    });

    const qs = useMemo(() => {
        const p = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v !== "" && v !== null && v !== undefined) p.set(k, String(v));
        });
        return p.toString();
    }, [filters]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/events/logs?${qs}`);
            setItems(data || []);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Event log alınamadı");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [qs]);

    useEffect(() => {
        const id = setInterval(() => {
            load();
        }, 10000);
        return () => clearInterval(id);
    }, [qs]);

    return (
        <div data-testid="admin-event-logs" className="space-y-6">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">Realtime Event Log</h1>
                    <p className="text-muted-foreground mt-1">Ülke, event türü, email ve tarih filtreleriyle canlı dönüşüm akışı.</p>
                </div>
                <Button onClick={load} data-testid="event-logs-refresh" disabled={loading} variant="outline" className="border-white/15">
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Yenile
                </Button>
            </div>

            <div className="rounded-2xl border border-white/10 bg-card p-4 grid md:grid-cols-6 gap-2" data-testid="event-logs-filters">
                <div className="space-y-1">
                    <Label>Event</Label>
                    <select
                        data-testid="event-filter-type"
                        className="w-full h-10 rounded-lg bg-background border border-white/10 px-3 text-sm"
                        value={filters.event_name}
                        onChange={(e) => setFilters((f) => ({ ...f, event_name: e.target.value }))}
                    >
                        {EVENT_OPTIONS.map((e) => <option key={e} value={e}>{e || "all"}</option>)}
                    </select>
                </div>

                <div className="space-y-1">
                    <Label>Ülke (TR, US)</Label>
                    <Input
                        data-testid="event-filter-country"
                        value={filters.country_code}
                        onChange={(e) => setFilters((f) => ({ ...f, country_code: e.target.value.toUpperCase() }))}
                        className="bg-background border-white/10"
                    />
                </div>

                <div className="space-y-1 md:col-span-2">
                    <Label>Email</Label>
                    <Input
                        data-testid="event-filter-email"
                        value={filters.user_email}
                        onChange={(e) => setFilters((f) => ({ ...f, user_email: e.target.value }))}
                        className="bg-background border-white/10"
                    />
                </div>

                <div className="space-y-1">
                    <Label>Başlangıç</Label>
                    <Input
                        data-testid="event-filter-start"
                        type="datetime-local"
                        value={filters.start_at}
                        onChange={(e) => setFilters((f) => ({ ...f, start_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                        className="bg-background border-white/10"
                    />
                </div>

                <div className="space-y-1">
                    <Label>Bitiş</Label>
                    <Input
                        data-testid="event-filter-end"
                        type="datetime-local"
                        value={filters.end_at}
                        onChange={(e) => setFilters((f) => ({ ...f, end_at: e.target.value ? new Date(e.target.value).toISOString() : "" }))}
                        className="bg-background border-white/10"
                    />
                </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-card overflow-hidden" data-testid="event-logs-table-wrap">
                <div className="px-4 py-3 border-b border-white/10 text-xs text-muted-foreground">
                    Son {filters.limit} event · auto-refresh 10sn
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="event-logs-table">
                        <thead className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-white/10">
                            <tr>
                                <th className="text-left p-3">Zaman</th>
                                <th className="text-left p-3">Event</th>
                                <th className="text-left p-3">Ülke</th>
                                <th className="text-left p-3">Email</th>
                                <th className="text-left p-3">Sayfa</th>
                                <th className="text-left p-3">Click IDs</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {items.map((it) => (
                                <tr key={it.id} data-testid={`event-row-${it.id}`}>
                                    <td className="p-3 text-xs font-mono">{new Date(it.created_at).toLocaleString()}</td>
                                    <td className="p-3">
                                        <p className="font-medium">{it.event_name}</p>
                                        <p className="text-[11px] text-muted-foreground">{it.content_name || "-"}</p>
                                    </td>
                                    <td className="p-3 text-xs font-mono">{it.country_code} <span className="text-muted-foreground">({it.country_source})</span></td>
                                    <td className="p-3 text-xs font-mono">{it.user_email || "-"}</td>
                                    <td className="p-3 text-xs font-mono">{it.page_path || "-"}</td>
                                    <td className="p-3 text-[11px] font-mono text-muted-foreground">
                                        g:{it.gclid ? "✓" : "-"} · fb:{it.fbclid ? "✓" : "-"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {items.length === 0 && (
                        <p className="p-8 text-center text-sm text-muted-foreground" data-testid="event-logs-empty">Filtreye uygun event bulunamadı.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
